import { useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BasicTable, Chip, DateFormattedUtils, MoneyFormattedUtils, PreviewExperience, PreviewExperienceDetail } from '@utriper/core-ui';
import { BookingOrigin, BookingStatus } from '@utriper/core';
import { PreviewService } from 'core/api/services/preview';

const noop = () => {};

const BookingTab = ({ bookings = [] }) => {
    const { t: translate, i18n } = useTranslation();

    const normalizedBookings = useMemo(() => {
        if (Array.isArray(bookings)) return bookings;
        return [];
    }, [bookings]);

    const hasBookings = normalizedBookings.length > 0;

    const columns = useMemo(
        () => [
            { align: 'left', val: 'code', label: translate('bookings.columns.code'), column: 'ci_code', style: { minWidth: '120px' } },
            { val: 'triper', label: translate('bookings.columns.triper'), column: 'ci_first_name', style: { minWidth: '180px' } },
            { val: 'email', label: 'Email', column: 'ci_email', style: { minWidth: '200px' } },
            { val: 'experience', label: translate('bookings.columns.experience'), style: { minWidth: '160px' } },
            { val: 'experienceDetail', label: translate('bookings.columns.experienceDetail'), column: 'ci_experience_detail_id', style: { minWidth: '160px' } },
            { val: 'mobilePhone', label: translate('bookings.columns.phone'), column: 'ci_mobile_phone', style: { minWidth: '150px' } },
            { val: 'date', label: translate('bookings.columns.date'), column: 'ci_date_utc', style: { minWidth: '200px' } },
            { val: 'createdOn', label: translate('bookings.columns.created_on'), column: 'ci_created_on', style: { minWidth: '200px' } },
            { align: 'center', val: 'quantity', label: translate('bookings.columns.quantity'), column: 'ci_quantity', style: { minWidth: '80px' } },
            { align: 'right', val: 'priceUSD', label: translate('bookings.columns.price_usd'), column: 'ci_price_usd', style: { minWidth: '140px' } },
            { align: 'right', val: 'price', label: translate('bookings.columns.price'), column: 'ci_price', style: { minWidth: '140px' } },
            { align: 'right', val: 'paidPrice', label: translate('bookings.columns.paid_amount'), column: 'ci_paid_price', style: { minWidth: '140px' } },
            { align: 'right', val: 'commission', label: translate('bookings.columns.commission'), style: { minWidth: '140px' } },
            { align: 'center', val: 'feeRate', label: translate('bookings.columns.fee_rate'), style: { minWidth: '100px' } },
            { align: 'left', val: 'paymentId', label: translate('bookings.columns.payment_id'), style: { minWidth: '120px' } },
            { align: 'left', val: 'origin', label: translate('bookings.columns.origin'), style: { minWidth: '140px' } },
            { align: 'left', val: 'status', label: translate('bookings.columns.status'), style: { minWidth: '140px' } },
        ],
        [translate]
    );

    const renderStatusChip = useCallback(
        (bookingStatus) => {
            const status = parseInt(bookingStatus, 10);
            if (Number.isNaN(status)) return bookingStatus ?? '';

            const colorLabel = status === 4 ? '#A6ba62' : status === 2 ? '#9bc2d1' : '#E39348';

            return <Chip style={{ color: colorLabel, fontWeight: 'bold' }} label={BookingStatus.getTranslatedName(BookingStatus.status, status, i18n.language)} />;
        },
        [i18n.language]
    );

    const tableRows = useMemo(() => {
        if (!hasBookings) return [];

        return normalizedBookings.map((booking) => {
            const originLabel = booking?.originId ? BookingOrigin.getTranslatedName(BookingOrigin.types, booking.originId, i18n.language)?.[0] : '';
            const formattedDate = DateFormattedUtils(booking?.dateUTC ?? booking?.date, i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                timeZone: booking?.timezone || 'UTC',
            });
            const formattedCreated = DateFormattedUtils(booking?.createdOn, i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                timeZone: booking?.timezone || 'UTC',
            });
            const triperName = booking?.firstName || booking?.lastName ? `${booking?.firstName ?? ''} ${booking?.lastName ?? ''}`.trim() : booking?.email ?? translate('reports.bookings_without_code');
            const experienceId = booking?.experienceId ?? booking?.experience?.id;
            const experienceDetailId = booking?.experienceDetailId ?? booking?.experienceDetail?.id;
            const experienceCell = experienceId ? <PreviewExperience id={experienceId} label="name" previewService={PreviewService} /> : booking?.experience?.name ?? translate('reports.bookings_without_experience');
            const experienceDetailCell = experienceDetailId ? <PreviewExperienceDetail id={experienceDetailId} label="description1" previewService={PreviewService} /> : booking?.experience?.description ?? booking?.experienceDetailId ?? '';

            return {
                ...booking,
                id: booking?.id,
                code: booking?.code ?? translate('reports.bookings_without_code'),
                triper: triperName,
                experience: experienceCell,
                experienceDetail: experienceDetailCell,
                date: formattedDate,
                createdOn: formattedCreated,
                price: `${booking?.currency ?? ''} ${MoneyFormattedUtils(booking?.price ?? 0)}`,
                priceUSD: booking?.priceUSD ? `USD ${MoneyFormattedUtils(booking?.priceUSD)}` : '',
                paidPrice: `${booking?.currency ?? ''} ${MoneyFormattedUtils(booking?.paidPrice ?? 0)}`,
                commission: `${booking?.currency ?? ''} ${MoneyFormattedUtils(booking?.commission ?? 0)}`,
                feeRate: booking?.feeRate ? `${booking.feeRate}%` : '-',
                origin: originLabel,
                status: renderStatusChip(booking?.status),
            };
        });
    }, [hasBookings, normalizedBookings, i18n.language, translate, renderStatusChip]);

    if (!hasBookings) {
        return (
            <Box
                sx={{
                    width: '100%',
                    minHeight: '240px',
                    border: '1px dashed #d1d1d1',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px',
                    gap: 1,
                }}
            >
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#6f6f6f' }}>
                    {translate('reports.bookings_tab')}
                </Typography>
                <Typography sx={{ color: '#9a9a9a' }}>{translate('reports.bookings_empty')}</Typography>
            </Box>
        );
    }

    return (
        <Box display="flex" flexDirection="column" gap={2} width="100%">
            <Typography variant="h6" fontWeight="bold">
                {translate('reports.bookings_tab')} ({normalizedBookings.length})
            </Typography>

            <BasicTable size="small" columns={columns} values={tableRows} orderBy="" direction="" handleSort={noop} />
        </Box>
    );
};

BookingTab.displayName = 'BookingTab';

export default BookingTab;
